'use client';

/**
 * DatePicker Component
 *
 * A styled date picker with custom calendar popup that matches
 * the MATIC application design system.
 *
 * Features:
 * - Custom styled calendar popup (no browser default)
 * - Orange accent color for selected dates
 * - Keyboard navigation support
 * - Click outside to close
 * - Consistent with dialog and input styling
 */

import {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {Calendar, ChevronLeft, ChevronRight} from 'lucide-react';
import {cn} from '@/matic/lib/utils';

type DatePickerProps = {
  /** Current value (YYYY-MM-DD format) */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Additional class names */
  className?: string;
  /** Minimum date (YYYY-MM-DD format) */
  minDate?: string;
  /** Maximum date (YYYY-MM-DD format) */
  maxDate?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Additional class names for the trigger button */
  buttonClassName?: string;
  /** Alignment of the calendar popup */
  popoverAlign?: 'left' | 'right' | 'center';
  /** Side of the trigger to render the calendar popup */
  popoverSide?: 'top' | 'bottom';
  /**
   * Custom trigger content. When provided, replaces the default value/placeholder
   * + calendar icon inside the trigger button (e.g. an icon-only ghost affordance).
   * All trigger behavior — ref, toggle, popover positioning — is unchanged.
   */
  triggerContent?: React.ReactNode;
  /** Accessible label / hover title for the trigger button. */
  triggerTitle?: string;
};

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  className,
  minDate,
  maxDate,
  disabled = false,
  buttonClassName,
  popoverAlign = 'left',
  popoverSide = 'bottom',
  triggerContent,
  triggerTitle,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      return new Date(value + 'T00:00:00');
    }

    return new Date();
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // The calendar is portaled to <body> (so a transformed/overflow ancestor —
  // e.g. a slide-in overlay panel — can't clip or mis-position the fixed popup),
  // so the popup is no longer a DOM descendant of containerRef. The outside-click
  // check below therefore tests both the trigger container and the popup.
  const popupRef = useRef<HTMLDivElement>(null);

  // Parse value to Date
  const selectedDate = useMemo(() => {
    if (!value) return null;
    return new Date(value + 'T00:00:00');
  }, [value]);

  // Update view date when value changes
  useEffect(() => {
    if (value) {
      setViewDate(new Date(value + 'T00:00:00'));
    }
  }, [value]);

  // Compute fixed position for the popover based on trigger button rect
  const computePopoverStyle = useCallback((): React.CSSProperties => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const calendarHeight = 340; // Approximate calendar height
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < calendarHeight && rect.top > calendarHeight;

    const calendarWidth = Math.max(rect.width, 288); // Min w-72

    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      width: calendarWidth,
    };

    if (openAbove) {
      style.bottom = window.innerHeight - rect.top + 4;
    } else {
      style.top = rect.bottom + 4;
    }

    if (popoverAlign === 'right') {
      style.right = window.innerWidth - rect.right;
    } else if (popoverAlign === 'center') {
      style.left = rect.left + rect.width / 2 - calendarWidth / 2;
    } else {
      style.left = rect.left;
    }

    return style;
  }, [popoverAlign]);

  // Close on click outside — checks the trigger container AND the portaled popup.
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Get days in month
  const daysInMonth = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Array<{date: Date; isCurrentMonth: boolean}> = [];

    // Add days from previous month to fill first week
    const startPadding = firstDay.getDay();
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({date, isCurrentMonth: false});
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      days.push({date, isCurrentMonth: true});
    }

    // Add days from next month to complete last week
    const endPadding = 7 - (days.length % 7);
    if (endPadding < 7) {
      for (let i = 1; i <= endPadding; i++) {
        const date = new Date(year, month + 1, i);
        days.push({date, isCurrentMonth: false});
      }
    }

    return days;
  }, [viewDate]);

  const handlePreviousMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleSelectDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    onChange?.(`${year}-${month}-${day}`);
    setIsOpen(false);
  };

  const isDateDisabled = (date: Date): boolean => {
    const dateString = formatDateToIso(date);
    if (minDate && dateString < minDate) return true;
    if (maxDate && dateString > maxDate) return true;
    return false;
  };

  const isDateSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return (
      date.getFullYear() === selectedDate.getFullYear() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getDate() === selectedDate.getDate()
    );
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const formatDisplayDate = (dateString: string): string => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateToIso = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Input Button */}
      <button
        ref={triggerRef}
        type="button"
        title={triggerTitle}
        aria-label={triggerTitle}
        onClick={() => {
          if (!disabled) {
            if (!isOpen) setPopoverStyle(computePopoverStyle());
            setIsOpen(!isOpen);
          }
        }}
        disabled={disabled}
        className={cn(
          triggerContent
            ? 'inline-flex items-center justify-center transition-colors'
            : 'flex w-full items-center justify-between rounded-md border-2 border-gray-200 bg-white px-3 py-2.5 text-left text-sm transition-colors hover:border-gray-300 focus:border-orange-500 focus:outline-none focus:ring-0',
          disabled && 'cursor-not-allowed opacity-50',
          !triggerContent && isOpen && 'border-orange-500',
          buttonClassName
        )}
      >
        {triggerContent ?? (
          <>
            <span className={cn(value ? 'text-gray-900' : 'text-gray-400')}>
              {value ? formatDisplayDate(value) : placeholder}
            </span>
            <Calendar className="h-4 w-4 text-gray-400" />
          </>
        )}
      </button>

      {/* Calendar Popup — portaled to <body> to escape transformed/overflow ancestors */}
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popupRef}
            className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
            style={popoverStyle}
          >
            {/* Header */}
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                onClick={handlePreviousMonth}
                className="rounded p-1 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm font-semibold text-gray-900">
                {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
              </span>
              <button
                type="button"
                onClick={handleNextMonth}
                className="rounded p-1 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Day Headers */}
            <div className="mb-1 grid grid-cols-7 gap-0">
              {DAYS.map((day) => (
                <div
                  key={day}
                  className="py-1 text-center text-xs font-medium text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-0">
              {daysInMonth.map(({date, isCurrentMonth}, index) => {
                const disabled = isDateDisabled(date);
                const selected = isDateSelected(date);
                const today = isToday(date);

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      if (!disabled) handleSelectDate(date);
                    }}
                    disabled={disabled}
                    className={cn(
                      'flex h-8 w-full items-center justify-center rounded text-sm transition-colors',
                      !isCurrentMonth && 'text-gray-300',
                      isCurrentMonth &&
                        !selected &&
                        !disabled &&
                        'text-gray-900 hover:bg-orange-50',
                      selected &&
                        'bg-orange-500 font-medium text-white hover:bg-orange-600',
                      today && !selected && 'font-semibold text-orange-600',
                      disabled && 'cursor-not-allowed text-gray-300'
                    )}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            {/* Footer Actions */}
            <div className="mt-3 flex gap-2 border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => {
                  handleSelectDate(new Date());
                }}
                className="flex-1 rounded-md px-3 py-1.5 text-center text-sm font-medium text-orange-600 transition-colors hover:bg-orange-50"
              >
                Today
              </button>
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    onChange?.('');
                    setIsOpen(false);
                  }}
                  className="flex-1 rounded-md px-3 py-1.5 text-center text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default DatePicker;
